import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import TermsAcceptance from "../components/TermsAcceptance";
import ProfileCompletion from "../components/ProfileCompletion";
import ApiService from "../services/ApiService";
import "./UserSetupPage.css";

const UserSetupPage = () => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();

  // Flow state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showProfileCompletion, setShowProfileCompletion] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);

  // Check onboarding status and determine what to show
  useEffect(() => {
    const checkOnboardingStatus = async () => {
      if (!currentUser) return;

      try {
        setCheckingStatus(true);

        // Get complete onboarding status
        const statusResponse = await ApiService.getOnboardingStatus();
        const status = statusResponse.data;

        console.log("Onboarding status:", status);

        // Check terms first
        if (!status.termsAccepted) {
          setShowTermsModal(true);
          setCheckingStatus(false);
          return;
        }

        // Check if profile completion is needed
        if (!status.profileCompleted) {
          console.log("Profile not completed, showing profile completion");
          setShowProfileCompletion(true);
        } else {
          console.log("Profile completed, redirecting to dashboard");
          navigate("/user-dashboard");
          return;
        }
      } catch (error) {
        console.error("Error checking onboarding status:", error);
        setShowTermsModal(true);
      } finally {
        setCheckingStatus(false);
      }
    };

    if (currentUser) {
      checkOnboardingStatus();
    }
  }, [currentUser, navigate]);

  const handleTermsAccepted = async () => {
    setShowTermsModal(false);

    try {
      // Accept terms in backend
      await ApiService.acceptTerms();

      // After terms are accepted, check if we need profile completion
      const statusResponse = await ApiService.getOnboardingStatus();
      const status = statusResponse.data;

      if (status.profileCompleted) {
        // Profile already completed, go to dashboard
        navigate("/user-dashboard");
      } else {
        // Profile not completed, show profile completion
        setShowProfileCompletion(true);
      }
    } catch (error) {
      console.error("Error handling terms acceptance:", error);
      setShowProfileCompletion(true); // Default to profile completion
    }
  };

  const handleProfileCompleted = async () => {
    try {
      setLoading(true);

      // Profile completed, proceed to dashboard
      navigate("/user-dashboard");
    } catch (error) {
      console.error("Error after profile completion:", error);
      setError("Failed to proceed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  // Show loading while checking status
  if (checkingStatus) {
    return (
      <div className="user-setup-container">
        <div className="user-setup-card">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="user-setup-container">
      {showTermsModal && (
        <TermsAcceptance
          onAccept={handleTermsAccepted}
          onCancel={() => navigate("/login")}
        />
      )}

      <header className="setup-header">
        <div className="header-content">
          <h1>Steam & Drive Setup</h1>
          <div className="user-info">
            <span>
              Welcome, {currentUser?.displayName || currentUser?.email}
            </span>
            <button onClick={handleLogout} className="logout-btn">
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="user-setup-card">
        {error && <div className="error-message">{error}</div>}

        {showProfileCompletion && (
          <div className="step-content">
            <h2>Complete Your Profile</h2>
            <p>
              Please provide your car details and select your community location
              to continue.
            </p>
            <ProfileCompletion onComplete={handleProfileCompleted} />
          </div>
        )}

        {loading && (
          <div className="loading-overlay">
            <div className="loading-spinner"></div>
            <p>Processing...</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserSetupPage;
